import { useEffect } from "react";

/**
 * Screen-reader announcements ("Reacted with 😂") through one shared, visually hidden polite live region.
 * Native: iOS `AccessibilityNotification.Announcement(text).post()`, Android `View.announceForAccessibility(text)`.
 */
let region: HTMLElement | undefined;
let pending: ReturnType<typeof setTimeout> | undefined;

function ensureRegion(): HTMLElement | undefined {
  if (typeof document === "undefined") return undefined;
  if (region === undefined || !region.isConnected) {
    region = document.createElement("div");
    region.className = "b-visually-hidden";
    region.setAttribute("role", "status");
    region.setAttribute("aria-live", "polite");
    region.setAttribute("aria-atomic", "true");
    region.setAttribute("data-testid", "announcer");
    document.body.appendChild(region);
  }
  return region;
}

function announce(text: string): void {
  const el = ensureRegion();
  if (el === undefined) return;
  // Clear, then set on a later tick: the same text twice in a row is announced twice.
  el.textContent = "";
  if (pending !== undefined) clearTimeout(pending);
  pending = setTimeout(() => {
    el.textContent = text;
  }, 100);
}

/** `announce(text)`: a polite screen-reader announcement, no visible UI. The live region exists from first mount. */
export function useAnnouncer(): (text: string) => void {
  useEffect(() => {
    ensureRegion();
  }, []);
  return announce;
}
